/**
 * Core HTTP client for the Ericius Go Backend (/api/v1/...)
 * Auto-injects Bearer token from localStorage, handles 401/403/429/402 status codes,
 * and provides typed JSON responses and secure file downloads.
 */

export class ApiError extends Error {
  status: number;
  code?: string;
  requiredTier?: string;
  retryAfter?: number;
  data?: any;

  constructor(
    status: number,
    message: string,
    code?: string,
    requiredTier?: string,
    retryAfter?: number,
    data?: any
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requiredTier = requiredTier;
    this.retryAfter = retryAfter;
    this.data = data;
  }
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined | null>;
}

export const API_BASE_URL = '/api/v1';

const inFlightRequests = new Map<string, Promise<any>>();

export async function apiClient<T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  if (method !== 'GET' || options.signal) {
    return executeRequest<T>(endpoint, options);
  }

  const token = localStorage.getItem('ericius_auth_token');
  const cacheKey = `${token || ''}:${endpoint}:${JSON.stringify(options.params || {})}`;

  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey) as Promise<T>;
  }

  const promise = executeRequest<T>(endpoint, options)
    .catch((err) => {
      inFlightRequests.delete(cacheKey);
      throw err;
    })
    .finally(() => {
      inFlightRequests.delete(cacheKey);
    });
  inFlightRequests.set(cacheKey, promise);
  return promise;
}

async function executeRequest<T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, headers: customHeaders, ...customOptions } = options;

  // 1. Build Query Parameters
  let url = endpoint.startsWith('http')
    ? endpoint
    : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    }
    const queryString = searchParams.toString();
    if (queryString) {
      url += (url.includes('?') ? '&' : '?') + queryString;
    }
  }

  // 2. Build Headers with Bearer Token
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(customHeaders as Record<string, string>),
  };

  if (!(customOptions.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const token = localStorage.getItem('ericius_auth_token');
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // 3. Execute Fetch
  let response: Response;
  try {
    response = await fetch(url, {
      ...customOptions,
      headers,
    });
  } catch (err: any) {
    throw new ApiError(0, err?.message || 'Network connection failure');
  }

  // 4. Handle HTTP Status Interceptors
  if (!response.ok) {
    let errorJson: any = {};
    try {
      errorJson = await response.json();
    } catch {
      // Non-JSON response payload
    }

    const message = errorJson.message || errorJson.error || response.statusText;
    const code = errorJson.code;
    const requiredTier = errorJson.required_tier;

    // Extract 429 Retry-After header
    let retryAfter: number | undefined;
    if (response.status === 429) {
      const retryHeader = response.headers.get('Retry-After');
      if (retryHeader) {
        retryAfter = parseInt(retryHeader, 10);
      }
    }

    // 401 Unauthorized: dispatch auth expired event
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('ericius:auth-unauthorized'));
    }

    // 403 Forbidden: dispatch permission denied event with required tier
    if (response.status === 403) {
      window.dispatchEvent(
        new CustomEvent('ericius:permission-denied', {
          detail: { code, requiredTier, message },
        })
      );
    }

    // 402 Payment Required: dispatch quota exceeded / upgrade needed event
    if (response.status === 402) {
      window.dispatchEvent(
        new CustomEvent('ericius:quota-exceeded', {
          detail: { code, message },
        })
      );
    }

    // 429 Rate Limit
    if (response.status === 429) {
      window.dispatchEvent(
        new CustomEvent('ericius:rate-limited', {
          detail: { retryAfter, message },
        })
      );
    }

    throw new ApiError(response.status, message, code, requiredTier, retryAfter, errorJson);
  }

  // 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return (await response.json()) as T;
}

/**
 * Downloads a file attachment (CSV / JSON export) using authenticated Bearer token.
 */
export async function downloadExportBlob(url: string, defaultFilename: string): Promise<void> {
  const token = localStorage.getItem('ericius_auth_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const fullUrl = url.startsWith('http')
    ? url
    : `${API_BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;

  const response = await fetch(fullUrl, { headers });
  if (!response.ok) {
    throw new ApiError(response.status, 'Export download failed');
  }

  // Extract filename from Content-Disposition if present
  let filename = defaultFilename;
  const disposition = response.headers.get('Content-Disposition');
  if (disposition && disposition.includes('filename=')) {
    const match = disposition.match(/filename="?([^";]+)"?/);
    if (match && match[1]) {
      filename = match[1];
    }
  }

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(blobUrl);
}
